import { authHeaders } from '@/lib/authHeaders';

/** Client-side fault reporting → server portal_errors. Best-effort: never
 *  throws, throttles duplicates so an error loop can't flood the log. */

export interface PortalError {
  id: string;
  email: string;
  at: string;
  kind: string;
  message: string;
  context: string;
  url: string;
  userAgent: string;
  stack: string;
  resolved: boolean;
}

const recent = new Map<string, number>();
const THROTTLE = 20_000;

/** Report a fault the user just hit (crash, failed action, API error…). */
export async function logPortalError(input: {
  message: string; kind?: string; context?: string; stack?: string;
}): Promise<void> {
  try {
    const message = (input.message || '').trim();
    if (!message) return;
    const key = `${input.kind || ''}|${message}`.slice(0, 200);
    const now = Date.now();
    if ((recent.get(key) ?? 0) > now - THROTTLE) return;
    recent.set(key, now);
    await fetch('/api/errors', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({
        kind: input.kind || 'runtime',
        message: message.slice(0, 2000),
        context: (input.context || '').slice(0, 400),
        url: location.pathname + location.search,
        stack: (input.stack || '').slice(0, 6000),
      }),
    });
  } catch { /* reporting must never throw */ }
}

/** Install global handlers so uncaught crashes and unhandled promise
 *  rejections are logged automatically. Call once on app start. */
export function installGlobalErrorLogging(): void {
  window.addEventListener('error', (e) => {
    const msg = e.message || String(e.error || 'Unknown error');
    if (/ResizeObserver loop|Script error\.?$/i.test(msg)) return; // benign noise
    void logPortalError({ kind: 'runtime', message: msg, context: e.filename ? `${e.filename}:${e.lineno}` : '', stack: e.error?.stack });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    const msg = reason instanceof Error ? reason.message : String(reason);
    /* ── UNA PETICIÓN CANCELADA NO ES UNA AVERÍA ──
       El filtro conocía «AbortError» y «The user aborted a request», pero no
       la frase que usan Chrome y Safari cuando quien cancela es el propio
       `AbortSignal` de react-query al cambiar de pantalla: «signal is aborted
       without reason». Gabriela la generó SEIS veces esta mañana sólo entrando
       y saliendo de /handover, y cada una quedó en el registro como si algo se
       hubiera roto. Un registro lleno de ruido es un registro que nadie lee, y
       ahí es donde luego hay que buscar los fallos de verdad.
       «Failed to fetch» se queda FUERA del filtro a propósito: eso sí es un
       corte de red y merece constar. */
    if (/AbortError|The user aborted a request|signal is aborted/i.test(msg)) return;
    /* ── Y «FAILED TO FETCH» SÓLO CUANDO PUEDE SER NUESTRO ──
       Gabriela: trece en un día, de las 03:38 a las 22:48, siempre en
       /handover — una pestaña abierta toda la noche en un portátil que se
       duerme y se despierta. Con la pestaña escondida o sin red no es el
       portal; con la pestaña delante y con red, sí, y entonces consta. */
    if (/Failed to fetch|Load failed|NetworkError/i.test(msg)
      && (document.visibilityState === 'hidden' || (typeof navigator !== 'undefined' && navigator.onLine === false))) return;
    void logPortalError({ kind: 'unhandledrejection', message: msg, stack: reason instanceof Error ? reason.stack : '' });
  });
}

/* ---- Admin viewer ---- */
export async function listPortalErrors(limit = 200): Promise<PortalError[]> {
  const r = await fetch(`/api/admin/errors?${new URLSearchParams({ limit: String(limit) })}`, { headers: await authHeaders() });
  if (!r.ok) throw new Error(`Errors ${r.status}`);
  return ((await r.json()) as { errors: PortalError[] }).errors;
}
export async function resolvePortalError(id: string, resolved: boolean): Promise<void> {
  const r = await fetch('/api/admin/errors/resolve', { method: 'POST', headers: { 'content-type': 'application/json', ...(await authHeaders()) }, body: JSON.stringify({ id, resolved }) });
  if (!r.ok) throw new Error(`Resolve ${r.status}`);
}
export async function clearResolvedErrors(): Promise<void> {
  const r = await fetch('/api/admin/errors/clear-resolved', { method: 'POST', headers: await authHeaders() });
  if (!r.ok) throw new Error(`Clear ${r.status}`);
}
export async function analyzePortalErrors(): Promise<string> {
  const r = await fetch('/api/admin/errors/analyze', { method: 'POST', headers: await authHeaders() });
  if (!r.ok) throw new Error(`Analyze ${r.status}${r.status === 500 ? ` — ${(await r.text()).slice(0, 200)}` : ''}`);
  return ((await r.json()) as { summary: string }).summary;
}

/** Close a whole grouped row (one message, many rows) in one call. */
export async function resolveManyErrors(ids: string[], resolved = true): Promise<void> {
  const r = await fetch('/api/admin/errors/resolve-many', { method: 'POST', headers: { 'content-type': 'application/json', ...(await authHeaders()) }, body: JSON.stringify({ ids, resolved }) });
  if (!r.ok) throw new Error(`Resolve ${r.status}`);
}
/** "I have read all of this." */
export async function resolveAllErrors(): Promise<number> {
  const r = await fetch('/api/admin/errors/resolve-all', { method: 'POST', headers: await authHeaders() });
  if (!r.ok) throw new Error(`Resolve all ${r.status}`);
  return ((await r.json()) as { count: number }).count;
}
/** Close everything older than two days. Run on arrival, silently. */
export async function sweepOldErrors(days = 2): Promise<number> {
  const r = await fetch('/api/admin/errors/sweep', { method: 'POST', headers: { 'content-type': 'application/json', ...(await authHeaders()) }, body: JSON.stringify({ days }) });
  if (!r.ok) throw new Error(`Sweep ${r.status}`);
  return ((await r.json()) as { count: number }).count;
}
export interface ErrorDiagnosis { meaning: string; cause: string; fix: string }
/** One fault, explained by Claude — only when somebody presses the button. */
export async function diagnosePortalError(id: string, lang: string): Promise<ErrorDiagnosis> {
  const r = await fetch('/api/admin/errors/diagnose', { method: 'POST', headers: { 'content-type': 'application/json', ...(await authHeaders()) }, body: JSON.stringify({ id, lang }) });
  if (!r.ok) throw new Error(`Diagnose ${r.status} — ${(await r.text()).slice(0, 200)}`);
  return (await r.json()) as ErrorDiagnosis;
}
